import { AsyncMultiSelectDropDown, AsyncSelect, Button } from '@wot/components';
import {
  ICategory,
  ICategoryBoard,
  ICategoryType,
  IGrades,
  IHobby,
  IHobbyType,
  ISkill,
  ISkillType,
  ISubject
} from '@wot/typings';
import _ from 'lodash';
import React, { SetStateAction, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACK_ARROW } from '../../assets/icons';

interface IStep2 {
  setStep: React.Dispatch<SetStateAction<'step-1' | 'step-2'>>;
  selected: ICategoryType;
  setSelected: React.Dispatch<SetStateAction<ICategoryType>>;
  handleStep1: (data: any) => void;
  requirementData: any;
  categoryData: ICategory<ISubject | IHobby | ISkill>[];
}

const getBoards = (categories: ICategory<ISubject>[]) => {
  let o = _.flatten(categories.map((i) => i.category.board));
  const y = _.uniq(o);
  return y.map((item) => item);
};

const getGradesFromBoard = (categories: ICategory<ISubject>[], board: ICategoryBoard) => {
  let o = _.flatten(
    categories.filter((i) => i.category.board.find((b) => b === board)).map((item) => item.category.grades)
  );
  return _.uniq(o.flatMap((item) => item));
};

const getSubjectsFromGrade = (categories: ICategory<ISubject>[], board: string, grade: IGrades) => {
  let o = _.flatten(
    categories
      .filter((i) => {
        let x = i.category.board.find((item) => item === board && i.category.grades.includes(grade));
        if (x) {
          return true;
        }
      })
      .map((item) => item.category.name)
  );
  return _.uniq(o.map((item) => item));
};

const getHobbies = (categories: ICategory<IHobby | ISkill>[]) => {
  let o = _.flatten(categories.map((i) => i.category.name));
  const y = _.uniq(o);
  return y.map((item) => item);
};

const getTypes = (categories: ICategory<IHobby | ISkill>[], name: string) => {
  let o = _.flatten(categories.filter((i) => i.category.name.includes(name)).map((item) => item.category.types));
  const y = _.uniq(o);
  return y.map((item) => item);
};

const getLevels = (categories: ICategory<IHobby | ISkill>[], name: string, type: string) => {
  if (type) {
    let o = _.flatten(
      categories.filter((i) => i.category.name === name)[0].category.types?.find((i) => i.name === type)?.levels
    );
    return _.uniq(o.map((item) => item));
  } else {
    let o = _.flatten(categories.filter((i) => i.category.name === name).map((item) => item.category.levels));
    return _.uniq(o.map((item) => item));
  }
};

const Step1: React.FC<IStep2> = ({ setStep, selected, setSelected, handleStep1, requirementData, categoryData }) => {
  const [error, setError] = useState(false);
  const [typeOptions, setTypeOptions] = useState<IHobbyType[] | ISkillType[]>([]);
  const navigate = useNavigate();

  const [category, setCategory] = useState<{
    board?: ICategoryBoard;
    grade?: IGrades;
    categoryList?: string[];
    type?: string;
    level?: string;
  }>(requirementData || {});

  useEffect(() => {
    setCategory(requirementData);
  }, [requirementData]);

  useEffect(() => {
    setCategory({});
    if (!categoryData) {
      return;
    }
    setTypeOptions([]);
  }, [selected, categoryData]);

  useEffect(() => {
    if (selected === 'subject' && category.board) {
      setCategory({
        ...category,
        grade: undefined,
        categoryList: []
      });
    }
  }, [category && category.board]);

  useEffect(() => {
    if (selected === 'subject' && category.board && category.grade) {
      setCategory({
        ...category,
        categoryList: []
      });
    }
  }, [category && category.grade]);

  function getSubjectOptions() {
    if (selected === 'subject' && category.board && category.grade) {
      let g = getSubjectsFromGrade(categoryData as ICategory<ISubject>[], category.board, category.grade);
      return g;
    } else {
      return [];
    }
  }

  function getLevelsOptions() {
    if (selected !== 'subject' && category.categoryList) {
      if (category.type && category.categoryList) {
        let g = getLevels(categoryData as ICategory<IHobby | ISkill>[], category.categoryList[0], category.type);
        return g as string[];
      } else {
        let g = getLevels(categoryData as ICategory<IHobby | ISkill>[], category.categoryList[0], '');
        return g as string[];
      }
    } else {
      return [];
    }
  }

  useEffect(() => {
    if (selected !== 'subject' && category.categoryList) {
      let g = getTypes(categoryData as ICategory<IHobby | ISkill>[], category.categoryList[0]);
      //  @ts-ignore
      setTypeOptions(g);
      setCategory({
        ...category,
        level: '',
        type: undefined
      });
    }
  }, [category && category.categoryList]);

  useEffect(() => {
    if (selected !== 'subject' && category.categoryList) {
      setCategory({
        ...category,
        level: ''
      });
    }
  }, [category && category.type]);

  const handle1 = (value: any) => {
    selected === 'subject'
      ? setCategory({
          ...category,
          board: value
        })
      : setCategory({
          ...category,
          categoryList: [value]
        });
  };
  const handle2 = (value: any) => {
    selected === 'subject'
      ? setCategory({
          ...category,
          grade: value
        })
      : setCategory({
          ...category,
          type: value
        });
  };
  const handle3 = (values: string[]) => {
    if (selected === 'subject' && category.categoryList) {
      setCategory({
        ...category,
        categoryList: [...values]
      });
    } else {
      // setCategory({ ...category, level: values });
    }
  };

  const handleLevel = (value: string) => {
    setCategory({
      ...category,
      level: value
    });
  };

  useEffect(() => {
    setError(false);
  }, [requirementData, selected]);

  const ContinueBtn = () => {
    if (selected === 'subject') {
      if (!category.board || !category.grade || (category.categoryList && category.categoryList.length === 0)) {
        setError(true);
        return;
      } else {
        setError(false);
        handleStep1({
          categoryType: selected,
          categoryList: category.categoryList,
          grade: category.grade,
          board: category.board
        });
        setStep('step-2');
        return;
      }
    }
    if (selected === 'hobby' || selected === 'skill') {
      if (!category.categoryList || (typeOptions.length > 0 && !category.type) || !category.level) {
        setError(true);
        return;
      } else {
        setError(false);
        handleStep1({
          categoryType: selected,
          categoryList: category.categoryList,
          type: category.type,
          level: category.level
        });
        setStep('step-2');
        return;
      }
    }
  };

  return (
    <div className="justify-between">
      <div>
        <div className="flex flex-row justify-start items-center my-8">
          <div
            className="w-8 h-8 hidden md:flex items-center justify-center bg-neutralBlue2 rounded-full cursor-pointer drop-shadow-md"
            onClick={() => navigate(-1)}
          >
            <img src={BACK_ARROW} alt="Go Back" />
          </div>
          <div className="w-full flex justify-start md:justify-center">
            <h5 className="text-2xl font-semibold">Post a requirement</h5>
          </div>
        </div>
        <div className="mt-5">
          <div className="flex flex-col">
            <label className="text-base font-medium">What do you want to learn?</label>
            <div className="flex md:flex-row flex-col justify-between md:my-5 gap-3 my-2">
              <div
                className={`${
                  selected === 'subject' ? 'bg-neutralBlue2' : 'bg-white'
                } border-2 border-neutralBlue1 basis-[30%] text-center px-2 py-3 text-base font-medium cursor-pointer rounded-lg`}
                onClick={() => setSelected('subject')}
              >
                Subject
              </div>
              <div
                className={`${
                  selected === 'hobby' ? 'bg-neutralBlue2' : 'bg-white'
                } border-2 border-neutralBlue1 basis-[30%] text-center px-2 py-3 text-base font-medium cursor-pointer rounded-lg`}
                onClick={() => setSelected('hobby')}
              >
                Hobby
              </div>
              <div
                className={`${
                  selected === 'skill' ? 'bg-neutralBlue2' : 'bg-white'
                } border-2 border-neutralBlue1 basis-[35%] text-center px-2 py-3 text-base font-medium cursor-pointer rounded-lg`}
                onClick={() => setSelected('skill')}
              >
                Skill Development
              </div>
            </div>
          </div>

          {/*Subject*/}
          {selected === 'subject' && (
            <>
              <div className="flex flex-col">
                <label className="text-base font-medium">Select the board you belong to</label>
                <div className="mt-3">
                  {/* <SelectDropDown
                    placeholder="Select Board"
                    value={category.board || ''}
                    onChange={(value) => handle1(value)}
                    options={boardOptions}
                  /> */}
                  <AsyncSelect
                    onChange={(newValue) => handle1(newValue)}
                    placeholder="Select Board"
                    value={category.board || ''}
                    callback={async () => getBoards(categoryData as ICategory<ISubject>[])}
                  />
                  {error && <p className="text-xs font-medium text-red-400">Please Select one of the Boards</p>}
                </div>
              </div>

              {category.board != undefined && (
                <div className="flex flex-col my-3">
                  <label className="text-base font-medium">Select the grade you are studying</label>
                  <div className="mt-3">
                    <AsyncSelect
                      onChange={(newValue) => handle2(newValue)}
                      placeholder="Select Grade"
                      value={category.grade || ''}
                      callback={async () =>
                        category.board ? getGradesFromBoard(categoryData as ICategory<ISubject>[], category.board) : []
                      }
                    />
                    {error && <p className="text-xs font-medium text-red-400">Please Select one of the Grades</p>}
                  </div>
                </div>
              )}

              {category.board && category.grade && (
                <div className="flex flex-col my-3">
                  <label className="text-base font-medium">Select the type of Subject you want to learn</label>
                  <div className="mt-3 text-black">
                    <AsyncMultiSelectDropDown
                      placeholder="Add Subjects"
                      values={category.categoryList || []}
                      onChange={(values) => handle3(values)}
                      callback={async () => getSubjectOptions()}
                      selectAll
                    />
                    {error && <p className="text-xs font-medium text-red-400">Please Select atleast one subject</p>}
                  </div>
                </div>
              )}
            </>
          )}

          {/*Hobbies*/}
          {selected === 'hobby' && (
            <>
              <div className="flex flex-col">
                <label className="text-base font-medium">Select the Hobby you want to learn</label>
                <div className="mt-3">
                  <AsyncSelect
                    onChange={(newValue) => handle1(newValue)}
                    placeholder="Select Hobby"
                    value={category?.categoryList && category.categoryList.length > 0 ? category.categoryList[0] : ''}
                    callback={async () => getHobbies(categoryData as ICategory<IHobby | ISkill>[])}
                  />
                  {error && <p className="text-xs font-medium text-red-400">Please Select atleast one hobby</p>}
                </div>
              </div>

              {category.categoryList && typeOptions.length > 0 && (
                <div className="flex flex-col my-3">
                  <label className="text-base font-medium">Select the type you want to learn</label>
                  <div className="mt-3">
                    <AsyncSelect
                      onChange={(newValue) => handle2(newValue)}
                      placeholder="Select Type"
                      value={category.type || ''}
                      // @ts-ignore
                      callback={async () =>
                        // @ts-ignore
                        category.categoryList
                          ? getTypes(categoryData as ICategory<IHobby | ISkill>[], category.categoryList[0]).map(
                              (x) => x?.name
                            )
                          : []
                      }
                    />
                    {error && <p className="text-xs font-medium text-red-400">Please Select one of the Types</p>}
                  </div>
                </div>
              )}

              {(typeOptions.length > 0 ? category.type : category.categoryList) && (
                <div className="flex flex-col my-3">
                  <label className="text-base font-medium">Select the level you want to learn</label>
                  <div className="mt-3">
                    <AsyncSelect
                      onChange={(newValue) => handleLevel(newValue)}
                      placeholder="Select Level"
                      value={category.level || ''}
                      callback={async () => getLevelsOptions() || []}
                    />
                    {error && <p className="text-xs font-medium text-red-400">Please Select one of the Levels</p>}
                  </div>
                </div>
              )}
            </>
          )}

          {/*Skill*/}
          {selected === 'skill' && (
            <>
              <div className="flex flex-col">
                <label className="text-base font-medium">Select the skill you want to learn</label>
                <div className="mt-3">
                  <AsyncSelect
                    onChange={(newValue) => handle1(newValue)}
                    placeholder="Select Skill"
                    value={category?.categoryList && category.categoryList.length > 0 ? category.categoryList[0] : ''}
                    callback={async () => getHobbies(categoryData as ICategory<IHobby | ISkill>[])}
                  />
                  {error && <p className="text-xs font-medium text-red-400">Please Select atleast one skill</p>}
                </div>
              </div>

              {category.categoryList && typeOptions.length > 0 && (
                <div className="flex flex-col my-3">
                  <label className="text-base font-medium">Select the type you want to learn</label>
                  <div className="mt-3">
                    <AsyncSelect
                      onChange={(newValue) => handle2(newValue)}
                      placeholder="Select Type"
                      value={category.type || ''}
                      // @ts-ignore
                      callback={async () =>
                        // @ts-ignore
                        category.categoryList
                          ? getTypes(categoryData as ICategory<IHobby | ISkill>[], category.categoryList[0]).map(
                              (x) => x?.name
                            )
                          : []
                      }
                    />
                    {error && <p className="text-xs font-medium text-red-400">Please Select one of the Types</p>}
                  </div>
                </div>
              )}

              {(typeOptions.length > 0 ? category.type : category.categoryList) && (
                <div className="flex flex-col my-3">
                  <label className="text-base font-medium">Select the level you want to learn</label>
                  <div className="mt-3">
                    <AsyncSelect
                      onChange={(newValue) => handleLevel(newValue)}
                      placeholder="Select Level"
                      value={category.level || ''}
                      callback={async () => getLevelsOptions() || []}
                    />
                    {error && <p className="text-xs font-medium text-red-400">Please Select one of the Levels</p>}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className="md:mt-10 mt-5">
        <Button block disabled={error} text="Continue" size="lg" onClick={() => ContinueBtn()} />
      </div>
    </div>
  );
};

export default Step1;
